import { createFileRoute, Link } from "@tanstack/react-router";
import superficiImg from "@/assets/superfici.webp";
import libera8 from "@/assets/libera8.webp.asset.json";
import libera3 from "@/assets/libera3.webp.asset.json";
import libera5 from "@/assets/libera5.webp.asset.json";
import { PreventivoForm } from "@/components/PreventivoForm";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { SocialLinks } from "@/components/Social";
import {
  ANNI_ESPERIENZA,
  ANNO_FONDAZIONE,
  EMAIL,
  TEL_ANTONIO,
  TEL_LUCA,
  WHATSAPP_URL,
  province,
} from "@/lib/site";

const URL_PAGINA = "https://artigianidelcotto.com/pulizia-trattamento-gres";
const TITOLO = "Pulizia gres porcellanato e pavimenti industriali | Lombardia";
const DESCRIZIONE =
  "Lavaggio professionale di gres porcellanato, ceramica e pavimenti industriali in capannoni, negozi e uffici. Rimozione velo di posa e antiscivolo. Preventivo gratuito: 339 751 7813.";
const IMMAGINE = `https://artigianidelcotto.com${libera8.url}`;

const servizi = [
  {
    titolo: "Lavaggio gres porcellanato",
    testo:
      "Il gres non assorbe, ma la sua superficie micro-ruvida trattiene detersivo, grasso e sporco: nasce così l'effetto opaco e appiccicoso. Lo laviamo con detergenti disincrostanti e monospazzola, riportando il colore originale.",
    voci: ["Gres opaco, strutturato ed effetto pietra", "Fughe scure e ingrigite", "Aloni di detersivo", "Case, uffici e negozi"],
  },
  {
    titolo: "Rimozione velo di posa e residui di cantiere",
    testo:
      "Dopo la posa restano velo cementizio, stucco e residui di colla che nessun lavaggio domestico toglie. Interveniamo con prodotti specifici acido tamponati, sicuri per il gres e per le fughe.",
    voci: ["Velo cementizio dopo posa", "Residui di stucco e colla", "Macchie di vernice e gesso", "Pulizia fine cantiere"],
  },
  {
    titolo: "Pavimenti industriali e superfici commerciali",
    testo:
      "Capannoni, magazzini, laboratori, mense e locali pubblici: grandi metrature in gres, ceramica tecnica o cemento, con sporco battuto, segni di gomma e traffico pesante. Lavoriamo con macchine a spazzole e programmiamo gli interventi fuori orario.",
    voci: ["Capannoni e magazzini", "Negozi, bar e ristoranti", "Uffici e parti comuni", "Interventi periodici programmati"],
  },
];

const passaggi = [
  {
    titolo: "Sopralluogo e prova di lavaggio",
    testo: "Verifichiamo tipo di gres, fughe e natura dello sporco con una prova su una piccola area. Preventivo scritto e gratuito, anche a metro quadro per grandi superfici.",
  },
  {
    titolo: "Lavaggio meccanico",
    testo: "Detergente specifico e monospazzola con disco adatto: lo sporco viene staccato dalla microporosità e aspirato, non solo spostato.",
  },
  {
    titolo: "Risciacquo e aspirazione",
    testo: "Risciacquiamo e aspiriamo l'acqua sporca. È il passaggio che manca ai lavaggi fai da te ed è il motivo per cui il pavimento torna a sporcarsi subito.",
  },
  {
    titolo: "Finitura o antiscivolo",
    testo: "Dove serve applichiamo un protettivo per fughe o un trattamento antiscivolo, utile in bagni, cucine professionali e aree di passaggio.",
  },
];

const galleria = [
  { src: libera8.url, alt: "Pavimento in gres porcellanato dopo lavaggio professionale in Lombardia" },
  { src: libera3.url, alt: "Pavimentazione interna lavata e riportata al colore originale" },
  { src: libera5.url, alt: "Superficie di grande metratura dopo pulizia professionale con monospazzola" },
];

const faq = [
  {
    q: "Cosa si intende per pavimento industriale?",
    a: "Superfici di grande metratura in ambienti di lavoro: capannoni, magazzini, laboratori, officine, ma anche negozi, bar, ristoranti e uffici. Sono pavimenti in gres tecnico, ceramica industriale o cemento, con sporco battuto, segni di gomma e traffico pesante, che richiedono macchine e prodotti diversi da quelli domestici.",
  },
  {
    q: "Perché il gres porcellanato resta opaco anche dopo averlo lavato?",
    a: "Quasi sempre è colpa del detersivo: i residui saponosi si accumulano nella microruvidità della superficie e trattengono lo sporco. Serve un lavaggio disincrostante con monospazzola e un risciacquo aspirato, non un altro giro di detergente.",
  },
  {
    q: "Il gres va trattato con un protettivo?",
    a: "Il gres porcellanato non è poroso e in genere non ha bisogno di impregnanti. Trattiamo invece le fughe, che sono la parte che assorbe, e su richiesta applichiamo un trattamento antiscivolo dove c'è rischio di scivolamento.",
  },
  {
    q: "Togliete il velo di posa dopo la ristrutturazione?",
    a: "Sì. È uno degli interventi che facciamo più spesso: rimuoviamo velo cementizio, residui di stucco, colla e vernice con prodotti acido tamponati che non danneggiano il gres né le fughe.",
  },
  {
    q: "Lavorate anche fuori orario nei locali commerciali?",
    a: "Sì, concordiamo gli interventi la sera, di notte o nei giorni di chiusura per non fermare l'attività. Per capannoni e negozi possiamo programmare una manutenzione periodica.",
  },
];

export const Route = createFileRoute("/pulizia-trattamento-gres")({
  component: PuliziaGres,
  head: () => ({
    meta: [
      { title: TITOLO },
      { name: "description", content: DESCRIZIONE },
      { property: "og:title", content: TITOLO },
      { property: "og:description", content: DESCRIZIONE },
      { property: "og:type", content: "article" },
      { property: "og:url", content: URL_PAGINA },
      { property: "og:image", content: IMMAGINE },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:image", content: IMMAGINE },
      { name: "geo.region", content: "IT-25" },
    ],
    links: [{ rel: "canonical", href: URL_PAGINA }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "HomeAndConstructionBusiness",
              "@id": "https://artigianidelcotto.com/#azienda",
              name: "Artigiani del Cotto",
              url: "https://artigianidelcotto.com/",
              telephone: "+39 339 751 7813",
              email: EMAIL,
              image: IMMAGINE,
              foundingDate: String(ANNO_FONDAZIONE),
              priceRange: "€€",
              address: {
                "@type": "PostalAddress",
                addressLocality: "Torbole Casaglia",
                addressRegion: "BS",
                addressCountry: "IT",
              },
              areaServed: [
                { "@type": "AdministrativeArea", name: "Lombardia" },
                ...province.map((p) => ({ "@type": "City", name: p })),
              ],
            },
            {
              "@type": "WebPage",
              "@id": `${URL_PAGINA}#pagina`,
              url: URL_PAGINA,
              name: TITOLO,
              description: DESCRIZIONE,
              inLanguage: "it-IT",
              primaryImageOfPage: { "@type": "ImageObject", url: IMMAGINE },
              isPartOf: {
                "@type": "WebSite",
                "@id": "https://artigianidelcotto.com/#sito",
                url: "https://artigianidelcotto.com/",
                name: "Artigiani del Cotto",
              },
              breadcrumb: { "@id": `${URL_PAGINA}#briciole` },
            },
            {
              "@type": "Service",
              "@id": `${URL_PAGINA}#servizio`,
              name: "Pulizia gres porcellanato e pavimenti industriali",
              serviceType: "Lavaggio professionale di gres porcellanato, ceramica e pavimenti industriali",
              category: "Manutenzione e trattamento pavimenti",
              description: DESCRIZIONE,
              url: URL_PAGINA,
              image: [IMMAGINE],
              provider: { "@id": "https://artigianidelcotto.com/#azienda" },
              areaServed: [
                { "@type": "AdministrativeArea", name: "Lombardia" },
                ...province.map((p) => ({ "@type": "City", name: p })),
              ],
              mainEntityOfPage: { "@id": `${URL_PAGINA}#pagina` },
              offers: {
                "@type": "Offer",
                priceCurrency: "EUR",
                availability: "https://schema.org/InStock",
                url: `${URL_PAGINA}#contatti`,
                description: "Sopralluogo e preventivo gratuiti in tutta la Lombardia.",
              },
              hasOfferCatalog: {
                "@type": "OfferCatalog",
                name: "Servizi su gres e pavimenti industriali",
                itemListElement: servizi.map((s) => ({
                  "@type": "Offer",
                  itemOffered: { "@type": "Service", name: s.titolo, description: s.testo },
                })),
              },
            },
            {
              "@type": "FAQPage",
              "@id": `${URL_PAGINA}#faq`,
              mainEntityOfPage: { "@id": `${URL_PAGINA}#pagina` },
              inLanguage: "it-IT",
              mainEntity: faq.map((f) => ({
                "@type": "Question",
                name: f.q,
                acceptedAnswer: { "@type": "Answer", text: f.a },
              })),
            },
            {
              "@type": "BreadcrumbList",
              "@id": `${URL_PAGINA}#briciole`,
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "Home", item: "https://artigianidelcotto.com/" },
                { "@type": "ListItem", position: 2, name: "Gres e pavimenti industriali", item: URL_PAGINA },
              ],
            },
          ],
        }),
      },
    ],
  }),
});

function PuliziaGres() {
  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />

      <main>
        <section className="gradient-warm py-16 text-background sm:py-20">
          <div className="mx-auto max-w-6xl px-5">
            <nav aria-label="Percorso" className="text-sm text-background/70">
              <Link to="/" className="underline-offset-4 hover:underline">Home</Link>
              <span className="px-2">/</span>
              <span>Gres e pavimenti industriali</span>
            </nav>
            <p className="eyebrow mt-6 text-sand">Dal {ANNO_FONDAZIONE} in tutta la Lombardia</p>
            <h1 className="mt-4 max-w-3xl text-4xl leading-[1.08] font-semibold sm:text-5xl">
              Pulizia gres porcellanato e pavimenti industriali
            </h1>
            <p className="mt-5 max-w-2xl text-base text-background/90 sm:text-lg">
              Lavaggio professionale di gres, ceramica e pavimentazioni di grande metratura: case,
              uffici, negozi, capannoni e magazzini. Rimozione velo di posa, sporco battuto e residui
              di detersivo. {ANNI_ESPERIENZA} di esperienza.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <a
                href="#contatti"
                className="rounded-xs bg-copper px-6 py-3 text-sm font-semibold text-accent-foreground transition-transform hover:-translate-y-0.5"
              >
                Richiedi un preventivo gratuito
              </a>
              <a
                href={`tel:${TEL_ANTONIO}`}
                className="rounded-xs border border-background/40 px-6 py-3 text-sm font-semibold transition-colors hover:bg-background/10"
              >
                Chiama Antonio: 339 751 7813
              </a>
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-6xl px-5 py-20">
          <div className="grid gap-12 lg:grid-cols-2">
            <div>
              <p className="eyebrow">Perché il gres si opacizza</p>
              <h2 className="mt-3 text-3xl font-semibold sm:text-4xl">
                Non è sporco: sono strati di detersivo.
              </h2>
              <p className="mt-5 text-base leading-relaxed text-muted-foreground">
                Il gres porcellanato non assorbe, ma la superficie è micro-ruvida. Ogni lavaggio con
                troppo detergente lascia una pellicola saponosa che trattiene polvere e grasso: il
                pavimento diventa opaco, a tratti appiccicoso, e le fughe si scuriscono.
              </p>
              <p className="mt-4 text-base leading-relaxed text-muted-foreground">
                Con un lavaggio disincrostante a monospazzola e un risciacquo aspirato rimuoviamo quegli
                strati fino alla superficie pulita. Su grandi metrature usiamo macchine lavasciuga e
                programmiamo gli interventi negli orari di chiusura.
              </p>
              <ul className="mt-8 space-y-3">
                {[
                  "Lavaggio gres porcellanato, ceramica e klinker",
                  "Rimozione velo di posa e residui di cantiere",
                  "Pulizia e protezione delle fughe",
                  "Capannoni, negozi, uffici e locali pubblici",
                ].map((t) => (
                  <li key={t} className="flex gap-3 text-sm">
                    <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-copper" />
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="self-start">
              <img
                src={superficiImg}
                alt="Pavimentazione lavata con attrezzatura professionale"
                width={1200}
                height={800}
                loading="eager"
                decoding="async"
                className="w-full rounded-sm object-cover shadow-[var(--shadow-soft)]"
              />
            </div>
          </div>
        </section>

        <section id="servizi" className="scroll-mt-20 bg-secondary/40 py-20">
          <div className="mx-auto max-w-6xl px-5">
            <p className="eyebrow">I nostri servizi</p>
            <h2 className="mt-3 max-w-2xl text-3xl font-semibold sm:text-4xl">
              Dal bagno di casa al capannone.
            </h2>
            <div className="mt-12 grid gap-6 md:grid-cols-3">
              {servizi.map((s, i) => (
                <article
                  key={s.titolo}
                  className="surface-warm flex flex-col rounded-sm border border-border p-7 transition-transform hover:-translate-y-1"
                >
                  <span className="font-display text-sm font-semibold text-copper">0{i + 1}</span>
                  <h3 className="mt-3 text-xl font-semibold">{s.titolo}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{s.testo}</p>
                  <ul className="mt-6 space-y-2 border-t border-border pt-5 text-sm">
                    {s.voci.map((v) => (
                      <li key={v} className="flex gap-2">
                        <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-copper" />
                        <span>{v}</span>
                      </li>
                    ))}
                  </ul>
                </article>
              ))}
            </div>
            <p className="mt-8 max-w-3xl text-sm leading-relaxed text-muted-foreground">
              Hai altri materiali nello stesso immobile? Vedi anche{" "}
              <Link to="/trattamento-marmo-pietre-graniglia" className="text-primary underline underline-offset-4">
                marmo, pietre e graniglia
              </Link>{" "}
              e{" "}
              <Link to="/pulizia-lavaggio-cotto" className="text-primary underline underline-offset-4">
                pulizia e lavaggio del cotto
              </Link>.
            </p>
          </div>
        </section>

        <section className="mx-auto max-w-6xl px-5 py-20">
          <p className="eyebrow">Il metodo</p>
          <h2 className="mt-3 max-w-2xl text-3xl font-semibold sm:text-4xl">
            Quattro passaggi, anche su grandi metrature.
          </h2>
          <ol className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {passaggi.map((p, i) => (
              <li key={p.titolo} className="border-t-2 border-copper pt-5">
                <span className="font-display text-sm font-semibold text-copper">Fase {i + 1}</span>
                <h3 className="mt-2 text-lg font-semibold">{p.titolo}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{p.testo}</p>
              </li>
            ))}
          </ol>
        </section>

        <section id="lavori" className="scroll-mt-20 bg-secondary/40 py-20">
          <div className="mx-auto max-w-6xl px-5">
            <p className="eyebrow">Alcuni lavori</p>
            <h2 className="mt-3 max-w-2xl text-3xl font-semibold sm:text-4xl">
              Pavimenti riportati al colore originale.
            </h2>
            <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {galleria.map((g) => (
                <img
                  key={g.alt}
                  src={g.src}
                  alt={g.alt}
                  width={1200}
                  height={900}
                  loading="lazy"
                  decoding="async"
                  className="aspect-4/3 w-full rounded-sm object-cover shadow-[var(--shadow-soft)]"
                />
              ))}
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-6xl px-5 py-20">
          <p className="eyebrow">Domande frequenti</p>
          <h2 className="mt-3 max-w-2xl text-3xl font-semibold sm:text-4xl">
            Quello che ci chiedono più spesso.
          </h2>
          <div className="mt-10 divide-y divide-border border-y border-border">
            {faq.map((f) => (
              <details key={f.q} className="group py-5">
                <summary className="cursor-pointer list-none text-base font-semibold marker:hidden">
                  {f.q}
                </summary>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{f.a}</p>
              </details>
            ))}
          </div>
        </section>

        <section className="gradient-warm py-16 text-background">
          <div className="mx-auto max-w-6xl px-5">
            <p className="eyebrow text-sand">Zone servite</p>
            <h2 className="mt-3 text-2xl font-semibold sm:text-3xl">
              Gres e pavimenti industriali in tutta la Lombardia.
            </h2>
            <p className="mt-4 max-w-2xl text-background/85">
              Con sede a Torbole Casaglia (Brescia) interveniamo in tutte le province lombarde, in
              abitazioni e in ambienti di lavoro. Sopralluogo gratuito e preventivo senza impegno.
            </p>
            <ul className="mt-8 flex flex-wrap gap-2">
              {province.map((p) => (
                <li
                  key={p}
                  className="rounded-xs border border-background/25 px-3 py-1.5 text-sm text-background/90"
                >
                  {p}
                </li>
              ))}
            </ul>
          </div>
        </section>

        <section id="contatti" className="mx-auto max-w-6xl scroll-mt-20 px-5 py-20">
          <div className="grid gap-12 md:grid-cols-2">
            <div>
              <p className="eyebrow">Preventivo</p>
              <h2 className="mt-3 text-3xl font-semibold sm:text-4xl">
                Dicci metri quadri e tipo di ambiente.
              </h2>
              <p className="mt-4 text-muted-foreground">
                Per capannoni e locali commerciali possiamo fare un prezzo a metro quadro.
                Sopralluogo e preventivo sono gratuiti e senza impegno.
              </p>
              <div className="mt-8 space-y-3">
                <a
                  href={`tel:${TEL_ANTONIO}`}
                  className="surface-warm flex items-center justify-between rounded-sm border border-border px-5 py-4 transition-colors hover:border-primary"
                >
                  <span className="font-medium">Antonio</span>
                  <span className="text-primary">+39 339 751 7813</span>
                </a>
                <a
                  href={`tel:${TEL_LUCA}`}
                  className="surface-warm flex items-center justify-between rounded-sm border border-border px-5 py-4 transition-colors hover:border-primary"
                >
                  <span className="font-medium">Luca</span>
                  <span className="text-primary">+39 351 720 4660</span>
                </a>
                <a
                  href={`mailto:${EMAIL}`}
                  className="surface-warm flex items-center justify-between rounded-sm border border-border px-5 py-4 transition-colors hover:border-primary"
                >
                  <span className="font-medium">Email</span>
                  <span className="text-primary">{EMAIL}</span>
                </a>
              </div>
              <div className="mt-6 flex flex-wrap items-center gap-4">
                <a
                  href={WHATSAPP_URL}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex rounded-xs bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground transition-colors hover:bg-sienna"
                >
                  Scrivi ad Antonio su WhatsApp
                </a>
                <SocialLinks />
              </div>
            </div>
            <PreventivoForm />
          </div>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}
